/**
 * Navigation prefetch + cache-seeding helpers.
 *
 * The in-app "clicking a tournament feels laggy" problem is a data waterfall:
 * the detail page mounts, *then* fires a cold GraphQL query, showing a blank
 * skeleton until AppSync/Lambda responds. Two complementary fixes live here:
 *
 *  1. prefetch-on-intent — warm the detail query when the user hovers / focuses
 *     / touches a list item, so the data is usually already in cache by the
 *     time the click navigates. Pure upside (React Query dedupes while fresh).
 *
 *  2. placeholderData seeding — the authenticated detail page (`/tournament/:id`)
 *     can render its header instantly from the summary already sitting in a
 *     cached list query, then hydrate. (The public hub `/t/:slug` needs the full
 *     public payload — bracket/matches — so it relies on prefetch, not seeding.)
 *
 * Keep the queryKeys/queryFns here in sync with the hooks in useQueries.ts.
 */

import { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryClient';
import type { TournamentDetail, TournamentWithClub } from './tournaments';

const DETAIL_STALE_TIME = 30 * 1000;
const SUMMARY_STALE_TIME = 15 * 1000;

function looksLikeTournamentSummary(x: unknown): x is TournamentWithClub {
  return (
    !!x &&
    typeof x === 'object' &&
    'id' in x &&
    'name' in x &&
    'status' in x &&
    'slug' in x
  );
}

/** Adapt a list item (TournamentWithClub) to the detail shape. The two types
 * share every core field; detail only adds optional extras we leave undefined
 * until the real fetch lands. */
function adaptSummaryToDetail(t: TournamentWithClub): TournamentDetail {
  return {
    id: t.id,
    name: t.name,
    clubId: t.clubId,
    seasonId: t.seasonId,
    slug: t.slug,
    date: t.date,
    time: t.time,
    location: t.location,
    status: t.status,
    registrationStatus: t.registrationStatus,
    playerCount: t.playerCount ?? 0,
    maxPlayers: t.maxPlayers,
    registrationOpenAt: t.registrationOpenAt,
    registrationCloseAt: t.registrationCloseAt,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
  };
}

/**
 * Find a tournament summary already cached by any list query (myTournaments,
 * clubTournaments, multiClubTournaments, …) and adapt it to the detail shape so
 * `/tournament/:id` can paint its header immediately. Returns undefined when the
 * tournament isn't in any cached list (then the normal skeleton shows).
 */
export function findCachedTournamentSummary(
  queryClient: QueryClient,
  tournamentId: string | undefined
): TournamentDetail | undefined {
  if (!tournamentId) return undefined;

  for (const [, data] of queryClient.getQueriesData<unknown>({})) {
    if (!data) continue;
    // List queries store either an array of items or an { items: [...] } object.
    const items: unknown[] = Array.isArray(data)
      ? data
      : Array.isArray((data as { items?: unknown }).items)
        ? ((data as { items: unknown[] }).items)
        : [];
    for (const item of items) {
      if (looksLikeTournamentSummary(item) && item.id === tournamentId) {
        return adaptSummaryToDetail(item);
      }
    }
  }
  return undefined;
}

/** Warm the authenticated tournament-detail query (`/tournament/:id`). */
export function prefetchTournament(
  queryClient: QueryClient,
  tournamentId: string | undefined
): void {
  if (!tournamentId) return;
  void queryClient.prefetchQuery({
    queryKey: queryKeys.tournament(tournamentId),
    queryFn: async () => {
      const { getTournament } = await import('./tournaments');
      return getTournament(tournamentId);
    },
    staleTime: DETAIL_STALE_TIME,
  });
}

/** Warm the public hub query (`/t/:slug`). Mirrors useTournamentSummary's
 * queryFn so the prefetched entry is consumed directly. */
export function prefetchTournamentSummary(
  queryClient: QueryClient,
  slug: string | undefined
): void {
  if (!slug) return;
  void queryClient.prefetchQuery({
    queryKey: queryKeys.tournamentSummary(slug),
    queryFn: async () => {
      const { getPublicTournament, calculateLiveMatchCount } = await import('./public-api');
      const data = await getPublicTournament(slug);
      if (data.error) {
        throw new Error(data.error.message);
      }
      return {
        publicData: data,
        liveMatchCount: calculateLiveMatchCount([...data.matches, ...data.bracket]),
      };
    },
    staleTime: SUMMARY_STALE_TIME,
  });
}

type PrefetchTarget = { slug?: string; id?: string };

/**
 * Returns event handlers to spread onto a tournament list item / link. On the
 * first pointer / focus / touch intent it warms the relevant detail query, so
 * the destination usually renders from cache. Safe to attach broadly.
 */
export function useTournamentPrefetch(target: PrefetchTarget) {
  const queryClient = useQueryClient();
  const { slug, id } = target;

  const fire = useCallback(() => {
    if (slug) prefetchTournamentSummary(queryClient, slug);
    if (id) prefetchTournament(queryClient, id);
  }, [queryClient, slug, id]);

  return {
    onMouseEnter: fire,
    onFocus: fire,
    onTouchStart: fire,
  };
}

